import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
  CheckCheck,
  ExternalLink,
  Eye,
  FileSpreadsheet,
  FileText,
  Mails,
  MoreVertical,
  Plus,
  Send,
  Undo2,
  UserMinus,
  XCircle,
} from "lucide-react";
import { useEventParticipants } from "../../hooks/useEventParticipants";
import { useInvitations } from "../../hooks/useInvitations";
import { useRemoveFromEvent } from "../../hooks/useUserManagement";
import { useAllHubSpotLinks } from "../../hooks/useHubSpot";
import { useEventVenue } from "../../hooks/useEventVenue";
import { useEvent } from "../../layouts/EventLayout";
import { useSnackbar } from "../../context/SnackbarContext";
import { PageHeader } from "../../components/PageHeader";
import { PhoneHeaderActions } from "../../components/phone/PhoneHeaderActions";
import type { OverflowAction } from "../../components/phone/OverflowMenu";
import { useIsPhone } from "../../hooks/useIsPhone";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { HubSpotLinkCell } from "../../components/HubSpotLinkCell";
import {
  HubSpotMatchDialog,
  type HubSpotMatchTarget,
} from "../../components/HubSpotMatchDialog";
import { DataTable, type Column } from "../../components/DataTable";
import { TwoStageFilter } from "../../components/TwoStageFilter";
import { CopyableCode } from "../../components/CopyableCode";
import {
  BulkSendConfirmDialog,
  type BulkSendCandidate,
} from "../../components/BulkSendConfirmDialog";
import { InvitationEmailStatusChip } from "../../components/InvitationEmailStatusChip";
import {
  InvitationEmailPreviewDialog,
  type InvitationEmailPreviewRecipient,
} from "../../components/InvitationEmailPreviewDialog";
import type { InvitationEmailPreviewEvent } from "../../components/InvitationEmailPreview";
import {
  deriveInvitationEmailState,
  NEEDS_EMAIL_STATES,
} from "../../utils/invitationEmailState";
import { FILTER_ROLES, titleCase } from "../../utils/filterOptions";
import { isSharePointConfigured, getEventRosterUrl } from "../../utils/sharepoint";
import { isHubSpotConfigured } from "../../utils/hubspot";
import {
  sendInvitationEmails,
  markInvitationEmailSentManually,
} from "../../services/invitationService";
import { formatDirectoryName } from "@event-planner/shared";
import { ExcelAttendeeImportDialog } from "./ExcelAttendeeImportDialog";
import type {
  Invitation,
  Role,
  SendInvitationEmailsResponse,
} from "@event-planner/shared";

type RowStatus = "active" | "pending";

interface AttendeeRow {
  /** PersonId of the attendee. */
  id: string;
  firstName: string;
  lastName: string;
  displayName: string;
  honorificPrefix: string | null;
  honorificSuffix: string | null;
  email: string;
  role: Role;
  status: RowStatus;
  photoURL: string | null;
  /** Per-event invitation code, from `eventParticipants.invitationCode`. */
  code: string | null;
}

/**
 * The one live (`sent`) invitation for a roster row. Match by personId
 * first; invitations written before ADR-193 carry no personId, so those
 * fall back to an email match. Two live invitations for one person cannot
 * arise any more (the issuer supersedes older ones), so `find` is safe.
 */
function findLiveInvitation(
  invitations: Invitation[],
  row: { id: string; email: string },
): Invitation | null {
  const byPerson = invitations.find(
    (i) => i.status === "sent" && i.personId === row.id,
  );
  if (byPerson) return byPerson;
  const email = row.email.toLowerCase();
  return (
    invitations.find(
      (i) =>
        i.status === "sent" &&
        !i.personId &&
        i.email.toLowerCase() === email,
    ) ?? null
  );
}

type FilterMode = "none" | "role" | "status";

interface FilterState {
  mode: FilterMode;
  role: Role | null;
  status: RowStatus | null;
}

const INITIAL_FILTER: FilterState = {
  mode: "none",
  role: null,
  status: null,
};


export function AttendeeListPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const isPhone = useIsPhone();
  const navigate = useNavigate();
  const { event } = useEvent();
  const { venue: eventVenue } = useEventVenue(event);
  const { showError, showSuccess } = useSnackbar();
  const { remove: removeFromEvent, loading: revoking } = useRemoveFromEvent();

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<FilterState>(INITIAL_FILTER);

  // Dialog + action state
  const [excelImportOpen, setExcelImportOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<AttendeeRow | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<AttendeeRow | null>(null);
  const [removing, setRemoving] = useState(false);
  /** Invitation row queued for the "mark / clear email sent manually" confirm. */
  const [markSentTarget, setMarkSentTarget] = useState<{
    invitationId: string;
    displayName: string;
    email: string;
    /** true = record a manual send, false = clear a previous manual mark. */
    marked: boolean;
  } | null>(null);
  const [markingSent, setMarkingSent] = useState(false);
  /** Which send dialog is open: the filter-driven bulk one or a single row. */
  const [sendMode, setSendMode] = useState<"bulk" | "single" | null>(null);
  const [singleSendCandidate, setSingleSendCandidate] =
    useState<BulkSendCandidate | null>(null);
  const [previewTarget, setPreviewTarget] =
    useState<InvitationEmailPreviewRecipient | null>(null);
  /** Per-row overflow menu anchor + the row it belongs to. */
  const [rowMenu, setRowMenu] = useState<{
    anchorEl: HTMLElement;
    row: AttendeeRow;
  } | null>(null);
  const [hubspotMatchTarget, setHubspotMatchTarget] =
    useState<HubSpotMatchTarget | null>(null);
  const {
    links: hubspotLinks,
    loading: hubspotLinksLoading,
    error: hubspotLinksError,
    refetch: refetchHubspotLinks,
  } = useAllHubSpotLinks(isHubSpotConfigured());

  // Active + pending participants for this event, joined with persons.
  const {
    participants,
    persons,
    loading: loadingParticipants,
    refetch: refetchParticipants,
  } = useEventParticipants(eventId, ["active", "pending"]);

  // All invitations for this event (for the Email column status + the
  // revoke action's invitationId lookup, both audit-only under ADR-136).
  const {
    data: invitations,
    loading: loadingInvitations,
    refetch: refetchInvitations,
  } = useInvitations(eventId);

  // Polling timer refs, cleared on unmount
  const refetchAll = useCallback(() => {
    refetchParticipants();
    refetchInvitations();
  }, [refetchParticipants, refetchInvitations]);

  const loading = loadingParticipants || loadingInvitations;

  // Build the roster rows by joining active + pending participants with
  // their person docs. ADR-136: the legacy users + contacts merge is
  // gone; one persons collection covers both.
  const allRows: AttendeeRow[] = useMemo(() => {
    const personById = new Map(persons.map((p) => [p.id, p] as const));
    const rows: AttendeeRow[] = [];

    for (const participant of participants) {
      // ADR-191: a row means "attendee of this event", whatever the role.
      // Admins access events through the claim and get a row only when
      // invited, so an admin row here is a real attendee with a real
      // invitation record.
      const person = personById.get(participant.personId);
      if (!person) continue;
      rows.push({
        id: person.id,
        firstName: person.firstName ?? "",
        lastName: person.lastName ?? "",
        displayName: person.displayName || person.email,
        honorificPrefix: person.honorificPrefix,
        honorificSuffix: person.honorificSuffix,
        email: person.email,
        role: participant.role,
        status: participant.status === "active" ? "active" : "pending",
        photoURL: person.photoURL,
        code: participant.invitationCode,
      });
    }

    return rows;
  }, [participants, persons]);

  /**
   * Resolves an attendee row to the invitation that drives its
   * email-sent column and bulk-send membership. Lookup is by personId
   * (stamped on every invitation since ADR-193), falling back to email
   * only for invitations written before the field existed. Every row is a
   * real invitation under ADR-191 (admins get rows only when invited), so
   * null means the invitation record is genuinely missing.
   */
  const resolveRowInvitation = useCallback(
    (row: AttendeeRow): Invitation | null => findLiveInvitation(invitations, row),
    [invitations],
  );

  // Apply filters + search
  const filteredRows = useMemo(() => {
    let result = allRows;

    if (filter.mode === "role" && filter.role) {
      result = result.filter((r) => r.role === filter.role);
    }
    if (filter.mode === "status" && filter.status) {
      result = result.filter((r) => r.status === filter.status);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (r) =>
          r.displayName.toLowerCase().includes(q) ||
          r.firstName.toLowerCase().includes(q) ||
          r.lastName.toLowerCase().includes(q) ||
          r.email.toLowerCase().includes(q),
      );
    }

    return result.sort((a, b) => {
      const last = a.lastName.localeCompare(b.lastName);
      if (last !== 0) return last;
      return a.firstName.localeCompare(b.firstName);
    });
  }, [allRows, filter, search]);

  const rowDirectoryName = useCallback(
    (row: AttendeeRow) =>
      formatDirectoryName({
        honorificPrefix: row.honorificPrefix,
        firstName: row.firstName,
        lastName: row.lastName,
        honorificSuffix: row.honorificSuffix,
        fallback: row.displayName || row.email,
      }),
    [],
  );

  /**
   * Resolves a row to a send-dialog candidate, or null when the row has no
   * invitation record.
   */
  const buildCandidate = useCallback(
    (row: AttendeeRow): BulkSendCandidate | null => {
      const inv = resolveRowInvitation(row);
      if (!inv) return null;
      const isActive = row.status === "active";
      return {
        invitationId: inv.id,
        displayName: rowDirectoryName(row),
        greetingName: row.displayName || null,
        email: row.email,
        code: isActive ? null : (inv.code ?? row.code),
        isActive,
        emailState: deriveInvitationEmailState(inv).state,
      };
    },
    [resolveRowInvitation, rowDirectoryName],
  );

  /** Bulk-send candidates: every filter-visible row with an invitation. */
  const bulkSendCandidates: BulkSendCandidate[] = useMemo(
    () =>
      filteredRows
        .map(buildCandidate)
        .filter((c): c is BulkSendCandidate => c !== null),
    [filteredRows, buildCandidate],
  );

  /** Event fields the email preview needs; mirrors the server's location join. */
  const previewEvent: InvitationEmailPreviewEvent | null = useMemo(() => {
    if (!event || !eventId) return null;
    const location =
      [eventVenue?.label, eventVenue?.city, eventVenue?.country]
        .filter(Boolean)
        .join(", ") || null;
    return {
      eventId,
      eventName: event.name,
      eventStartDate: event.startDate,
      eventEndDate: event.endDate,
      eventLocation: location,
    };
  }, [event, eventId, eventVenue]);

  async function handleRemoveConfirm() {
    if (!removeTarget || !eventId) return;
    setRemoving(true);
    try {
      await removeFromEvent({ eventId, personId: removeTarget.id });
      showSuccess(`${removeTarget.displayName} removed from event.`);
      setRemoveTarget(null);
      refetchAll();
    } catch (err) {
      showError(
        err instanceof Error ? err.message : "Failed to remove member.",
      );
    } finally {
      setRemoving(false);
    }
  }

  async function handleRevokeConfirm() {
    if (!revokeTarget || !eventId) return;
    try {
      // Find the live invitation for this person (personId first, email
      // fallback for pre-ADR-193 rows); the callable still requires an
      // invitationId on the revoke branch.
      const inv = findLiveInvitation(invitations, revokeTarget);
      if (!inv) {
        showError(
          "No matching invitation to revoke. The person may have already been removed.",
        );
        setRevokeTarget(null);
        return;
      }
      await removeFromEvent({ eventId, invitationId: inv.id });
      showSuccess(`Invitation for ${revokeTarget.displayName} revoked.`);
      setRevokeTarget(null);
      refetchAll();
    } catch (err) {
      showError(
        err instanceof Error ? err.message : "Failed to revoke invitation.",
      );
    }
  }

  async function handleMarkSentConfirm() {
    if (!markSentTarget || !eventId) return;
    setMarkingSent(true);
    try {
      await markInvitationEmailSentManually({
        eventId,
        invitationIds: [markSentTarget.invitationId],
        marked: markSentTarget.marked,
      });
      showSuccess(
        markSentTarget.marked
          ? `Marked invitation for ${markSentTarget.email} as sent.`
          : `Cleared the manual mark for ${markSentTarget.email}.`,
      );
      setMarkSentTarget(null);
      refetchInvitations();
    } catch (err) {
      showError(
        err instanceof Error ? err.message : "Failed to mark invitation as sent.",
      );
    } finally {
      setMarkingSent(false);
    }
  }

  /**
   * Fires the send. The dialog renders the response as its result screen
   * and closes on Done. No refetch: the invitations subscription is live,
   * so each row's chip moves from "sending" to "delivered" / "failed" on
   * its own as the transport reports back, and participants are untouched
   * by a send.
   */
  async function handleSendConfirm(
    invitationIds: string[],
  ): Promise<SendInvitationEmailsResponse> {
    if (!eventId) throw new Error("No event selected.");
    return sendInvitationEmails({ eventId, invitationIds });
  }

  function closeSendDialog() {
    setSendMode(null);
    setSingleSendCandidate(null);
  }

  function openSingleSend(row: AttendeeRow) {
    const candidate = buildCandidate(row);
    if (!candidate) {
      showError("No invitation record for this person.");
      return;
    }
    setSingleSendCandidate(candidate);
    setSendMode("single");
  }

  function openPreview(row: AttendeeRow) {
    const inv = resolveRowInvitation(row);
    const isActive = row.status === "active";
    setPreviewTarget({
      displayName: rowDirectoryName(row),
      greetingName: row.displayName || null,
      email: row.email,
      code: isActive ? null : (inv?.code ?? row.code),
      isActive,
    });
  }

  /** Preview from inside the send dialog: same data the send would use. */
  function previewCandidate(candidate: BulkSendCandidate) {
    setPreviewTarget({
      displayName: candidate.displayName,
      greetingName: candidate.greetingName,
      email: candidate.email,
      code: candidate.code,
      isActive: candidate.isActive,
    });
  }

  function handleRowClick(row: AttendeeRow) {
    navigate(`/users/${row.id}`);
  }

  const columns: Column<AttendeeRow>[] = [
    {
      id: "name",
      label: "Name",
      width: "30%",
      render: (row) => {
        const directoryName = formatDirectoryName({
          honorificPrefix: row.honorificPrefix,
          firstName: row.firstName,
          lastName: row.lastName,
          honorificSuffix: row.honorificSuffix,
          fallback: row.displayName || row.email,
        });
        return (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              src={row.photoURL ?? undefined}
              alt={row.displayName}
              sx={{ width: 34, height: 34 }}
            >
              {row.firstName?.[0] || row.displayName[0] || "?"}
            </Avatar>
            <Box>
              <Typography sx={{ fontSize: "0.938rem", fontWeight: 500, lineHeight: 1.3 }}>
                {directoryName}
              </Typography>
              {row.email && (
                <Typography sx={{ fontSize: "0.813rem", color: "text.secondary", lineHeight: 1.3 }}>
                  {row.email}
                </Typography>
              )}
            </Box>
          </Stack>
        );
      },
    },
    {
      id: "itinerary",
      label: "Itinerary",
      width: "8%",
      phone: "hidden",
      align: "center",
      render: (row) => (
        <Tooltip title="View experience report">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/events/${eventId}/attendees/${row.id}/report`);
            }}
            aria-label={`Experience report for ${row.displayName}`}
            data-testid={`view-report-${row.id}`}
          >
            <FileText size={16} />
          </IconButton>
        </Tooltip>
      ),
    },
    ...(isHubSpotConfigured()
      ? ([
          {
            id: "hubspot",
            label: "HubSpot",
            width: "96px",
            phone: "chips",
            align: "center",
            render: (row) => (
              <HubSpotLinkCell
                personId={row.id}
                email={row.email}
                displayName={row.displayName}
                contactId={hubspotLinks.get(row.id)}
                loading={hubspotLinksLoading}
                error={hubspotLinksError}
                onRetry={refetchHubspotLinks}
                onOpenMatch={() =>
                  setHubspotMatchTarget({
                    personId: row.id,
                    email: row.email,
                    displayName: row.displayName,
                  })
                }
              />
            ),
          },
        ] satisfies Column<AttendeeRow>[])
      : []),
    {
      id: "role",
      label: "Role",
      width: "12%",
      phone: "chips",
      render: (row) => (
        <Chip
          label={row.role}
          size="small"
          sx={{ textTransform: "capitalize", fontSize: "0.813rem" }}
        />
      ),
    },
    {
      id: "status",
      label: "Account Status",
      width: "14%",
      phone: "chips",
      render: (row) =>
        row.status === "active" ? (
          <Tooltip title="User has downloaded the app and signed in" arrow>
            <Chip
              label="Active"
              size="small"
              color="success"
              variant="outlined"
              sx={{ fontSize: "0.813rem" }}
              data-testid={`attendee-status-active-${row.id}`}
            />
          </Tooltip>
        ) : (
          <Tooltip title="User has not yet downloaded and signed in on the mobile app" arrow>
            <Chip
              label="Pending"
              size="small"
              color="warning"
              variant="outlined"
              sx={{ fontSize: "0.813rem" }}
              data-testid={`attendee-status-pending-${row.id}`}
            />
          </Tooltip>
        ),
    },
    {
      id: "code",
      label: "Code",
      width: "13%",
      render: (row) => {
        // Only pending rows carry codes; active members already have an
        // account. Source under ADR-136 is `eventParticipants.invitationCode`.
        if (row.status !== "pending") return <CopyableCode code={null} />;
        return (
          <CopyableCode
            code={row.code}
            testId={`attendee-code-${row.id}`}
          />
        );
      },
    },
    {
      id: "email",
      label: "Email",
      width: "16%",
      render: (row) => (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          onClick={(e) => e.stopPropagation()}
        >
          <InvitationEmailStatusChip
            invitation={resolveRowInvitation(row)}
            testIdSuffix={row.id}
          />
          <Tooltip title="Email actions">
            <IconButton
              size="small"
              onClick={(e) => setRowMenu({ anchorEl: e.currentTarget, row })}
              aria-label={`Email actions for ${row.email}`}
              data-testid={`attendee-row-menu-${row.id}`}
            >
              <MoreVertical size={16} />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
    {
      id: "actions",
      label: "Actions",
      width: "15%",
      phone: "hidden",
      align: "right",
      render: (row) => (
        <Stack
          direction="row"
          spacing={0.5}
          justifyContent="flex-end"
          onClick={(e) => e.stopPropagation()}
        >
          {row.status === "active" ? (
            <Button
              size="small"
              color="error"
              startIcon={<UserMinus size={15} />}
              onClick={() => setRemoveTarget(row)}
              data-testid={`remove-member-${row.id}`}
              sx={{ textTransform: "none", fontSize: "0.813rem" }}
            >
              Remove
            </Button>
          ) : (
            <Button
              size="small"
              color="error"
              startIcon={<XCircle size={15} />}
              onClick={() => setRevokeTarget(row)}
              disabled={revoking}
              aria-label={`Revoke invitation for ${row.email}`}
              data-testid={`revoke-invitation-${row.id}`}
              sx={{ textTransform: "none", fontSize: "0.813rem" }}
            >
              Revoke
            </Button>
          )}
        </Stack>
      ),
    },
  ];

  // Phone: the email status chip joins the chip row only on pending rows,
  // where it is actionable (people already in the app need no invitation);
  // its actions move to the row menu. The code shows only on pending rows,
  // as its own line.
  const phoneColumns: Column<AttendeeRow>[] = columns.map((col) => {
    if (col.id === "email") {
      return {
        ...col,
        phone: "chips",
        render: (row) =>
          row.status === "pending" ? (
            <InvitationEmailStatusChip
              invitation={resolveRowInvitation(row)}
              testIdSuffix={row.id}
            />
          ) : null,
      };
    }
    if (col.id === "code") {
      return {
        ...col,
        phone: "secondary",
        render: (row) =>
          row.status === "pending" && row.code ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <span>Code</span>
              <CopyableCode code={row.code} testId={`attendee-code-${row.id}`} />
            </Stack>
          ) : null,
      };
    }
    return col;
  });

  // Phone: every row action in one ⋮ menu: email actions, report, remove.
  function attendeeRowActions(row: AttendeeRow): OverflowAction[] {
    const actions: OverflowAction[] = [];
    const inv = resolveRowInvitation(row);
    if (inv) {
      const summary = deriveInvitationEmailState(inv);
      const needsEmail = NEEDS_EMAIL_STATES.has(summary.state);
      actions.push(
        {
          label: needsEmail ? "Send email" : "Resend email",
          icon: <Send size={16} />,
          onClick: () => openSingleSend(row),
          testId: `row-menu-send-${row.id}`,
        },
        {
          label: "Preview email",
          icon: <Eye size={16} />,
          onClick: () => openPreview(row),
          testId: `row-menu-preview-${row.id}`,
        },
        {
          label: summary.markedManuallyAt ? "Clear manual mark" : "Mark as sent manually",
          icon: summary.markedManuallyAt ? <Undo2 size={16} /> : <CheckCheck size={16} />,
          onClick: () =>
            setMarkSentTarget({
              invitationId: inv.id,
              displayName: row.displayName,
              email: row.email,
              marked: !summary.markedManuallyAt,
            }),
          testId: `row-menu-manual-${row.id}`,
        },
      );
    }
    actions.push({
      label: "Experience report",
      icon: <FileText size={16} />,
      onClick: () => navigate(`/events/${eventId}/attendees/${row.id}/report`),
      testId: `view-report-${row.id}`,
    });
    actions.push(
      row.status === "active"
        ? {
            label: "Remove from event",
            icon: <UserMinus size={16} />,
            onClick: () => setRemoveTarget(row),
            destructive: true,
            testId: `remove-member-${row.id}`,
          }
        : {
            label: "Revoke invitation",
            icon: <XCircle size={16} />,
            onClick: () => setRevokeTarget(row),
            disabled: revoking,
            destructive: true,
            testId: `revoke-invitation-${row.id}`,
          },
    );
    return actions;
  }

  return (
    <Box>
      <PageHeader
        title="Attendees"
        action={
          isPhone ? (
            <PhoneHeaderActions
              primary={
                <Button
                  variant="contained"
                  startIcon={<Plus size={18} />}
                  onClick={() =>
                    eventId && navigate(`/events/${eventId}/attendees/new`)
                  }
                  data-testid="new-attendee-button"
                >
                  Add
                </Button>
              }
              more={[
                {
                  label: "Import from Excel",
                  icon: <FileSpreadsheet size={18} />,
                  onClick: () => setExcelImportOpen(true),
                  testId: "import-attendees-excel-button",
                },
                ...(isSharePointConfigured()
                  ? [
                      {
                        label: "Open in SharePoint",
                        icon: <ExternalLink size={18} />,
                        onClick: () =>
                          window.open(getEventRosterUrl(event?.name), "_blank"),
                        testId: "open-sharepoint-roster-button",
                      },
                    ]
                  : []),
              ]}
            />
          ) : (
          <Stack direction="row" spacing={1}>
            {isSharePointConfigured() && (
              <Tooltip title="Open this event's roster in SharePoint">
                <Button
                  variant="outlined"
                  startIcon={<ExternalLink size={18} />}
                  onClick={() =>
                    window.open(getEventRosterUrl(event?.name), "_blank")
                  }
                  data-testid="open-sharepoint-roster-button"
                >
                  Open in SharePoint
                </Button>
              </Tooltip>
            )}
            <Button
              variant="outlined"
              startIcon={<FileSpreadsheet size={18} />}
              onClick={() => setExcelImportOpen(true)}
              data-testid="import-attendees-excel-button"
            >
              Import from Excel
            </Button>
            <Button
              variant="contained"
              startIcon={<Plus size={18} />}
              onClick={() =>
                eventId && navigate(`/events/${eventId}/attendees/new`)
              }
              data-testid="new-attendee-button"
            >
              Add Attendee
            </Button>
          </Stack>
          )
        }
      />

      <TwoStageFilter<Role | RowStatus>
        search={search}
        onSearchChange={setSearch}
        searchTestId="attendee-search-input"
        noneLabel="All attendees"
        modes={[
          {
            value: "role",
            label: "Filter by role",
            placeholder: "Select role…",
            options: FILTER_ROLES.map((r) => ({ value: r, label: titleCase(r) })),
          },
          {
            value: "status",
            label: "Filter by account status",
            placeholder: "Select status…",
            options: [
              { value: "active", label: "Active" },
              { value: "pending", label: "Pending" },
            ],
          },
        ]}
        mode={filter.mode}
        value={filter.mode === "role" ? filter.role : filter.status}
        onChange={({ mode, value }) => {
          const m = mode as FilterMode;
          if (m === "role") {
            setFilter({ mode: m, role: (value as Role) ?? null, status: null });
          } else if (m === "status") {
            setFilter({
              mode: m,
              role: null,
              status: (value as RowStatus) ?? null,
            });
          } else {
            setFilter(INITIAL_FILTER);
          }
        }}
        modeTestId="filter-mode-select"
        valueTestId="filter-value-select"
        trailing={
          <Tooltip
            title={
              bulkSendCandidates.length === 0
                ? "No one in the current view has an invitation record"
                : "Choose who gets an invitation email"
            }
          >
            <span>
              <Button
                variant={isPhone ? "outlined" : "contained"}
                startIcon={<Mails size={16} />}
                onClick={() => setSendMode("bulk")}
                disabled={bulkSendCandidates.length === 0}
                data-testid="bulk-send-button"
              >
                Bulk Invite
              </Button>
            </span>
          </Tooltip>
        }
      />

      <DataTable
        columns={isPhone ? phoneColumns : columns}
        data={filteredRows}
        loading={loading}
        emptyMessage="No attendees yet. Invite a user to populate the event roster."
        ariaLabel="Attendees table"
        getRowKey={(row) => row.id}
        onRowClick={handleRowClick}
        phoneRowActions={attendeeRowActions}
        phoneRowActionsLabel={(row) => `Actions for ${row.displayName}`}
      />

      {eventId && (
        <ExcelAttendeeImportDialog
          open={excelImportOpen}
          eventId={eventId}
          onClose={() => setExcelImportOpen(false)}
          onImported={refetchAll}
        />
      )}

      <ConfirmDialog
        open={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        onConfirm={handleRemoveConfirm}
        title="Remove member"
        message={`Remove ${removeTarget?.displayName ?? "this user"} from the event? This will delete their session assignments, RSVPs, check-ins, and survey responses for this event.`}
        confirmLabel="Remove"
        loading={removing}
      />

      <ConfirmDialog
        open={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onConfirm={handleRevokeConfirm}
        title="Revoke invitation"
        message={`Revoke the invitation for ${revokeTarget?.displayName ?? revokeTarget?.email ?? "this person"}? They will no longer be able to claim this invitation. The person record is preserved and can be re-invited later.`}
        confirmLabel="Revoke"
        loading={revoking}
      />

      <ConfirmDialog
        open={!!markSentTarget}
        onClose={() => setMarkSentTarget(null)}
        onConfirm={handleMarkSentConfirm}
        title={markSentTarget?.marked === false ? "Clear manual mark?" : "Mark email as sent?"}
        message={
          markSentTarget?.marked === false
            ? `Remove the "sent manually" mark for ${markSentTarget?.email ?? "this person"}. Use this if it was recorded by mistake.`
            : `Confirm that you've sent the invitation email to ${markSentTarget?.email ?? "this person"} outside the app. This records the send in the audit trail without delivering an email.`
        }
        confirmLabel={markSentTarget?.marked === false ? "Clear Mark" : "Mark Sent"}
        loading={markingSent}
      />

      <BulkSendConfirmDialog
        open={sendMode !== null}
        onClose={closeSendDialog}
        mode={sendMode ?? "bulk"}
        candidates={
          sendMode === "single" && singleSendCandidate
            ? [singleSendCandidate]
            : bulkSendCandidates
        }
        onPreview={previewCandidate}
        onConfirm={handleSendConfirm}
      />

      <InvitationEmailPreviewDialog
        open={!!previewTarget}
        onClose={() => setPreviewTarget(null)}
        event={previewEvent}
        recipient={previewTarget}
      />

      <Menu
        anchorEl={rowMenu?.anchorEl ?? null}
        open={!!rowMenu}
        onClose={() => setRowMenu(null)}
        data-testid="attendee-row-menu"
      >
        {(() => {
          if (!rowMenu) return null;
          const { row } = rowMenu;
          const inv = resolveRowInvitation(row);
          if (!inv) {
            return (
              <MenuItem disabled>
                <ListItemText primary="No invitation record for this person" />
              </MenuItem>
            );
          }
          const summary = deriveInvitationEmailState(inv);
          const needsEmail = NEEDS_EMAIL_STATES.has(summary.state);
          const close = () => setRowMenu(null);
          return [
            <MenuItem
              key="send"
              onClick={() => {
                close();
                openSingleSend(row);
              }}
              data-testid={`row-menu-send-${row.id}`}
            >
              <ListItemIcon>
                <Send size={16} />
              </ListItemIcon>
              <ListItemText primary={needsEmail ? "Send email" : "Resend email"} />
            </MenuItem>,
            <MenuItem
              key="preview"
              onClick={() => {
                close();
                openPreview(row);
              }}
              data-testid={`row-menu-preview-${row.id}`}
            >
              <ListItemIcon>
                <Eye size={16} />
              </ListItemIcon>
              <ListItemText primary="Preview email" />
            </MenuItem>,
            <Divider key="divider" />,
            <MenuItem
              key="manual"
              onClick={() => {
                close();
                setMarkSentTarget({
                  invitationId: inv.id,
                  displayName: row.displayName,
                  email: row.email,
                  marked: !summary.markedManuallyAt,
                });
              }}
              data-testid={`row-menu-manual-${row.id}`}
            >
              <ListItemIcon>
                {summary.markedManuallyAt ? <Undo2 size={16} /> : <CheckCheck size={16} />}
              </ListItemIcon>
              <ListItemText
                primary={
                  summary.markedManuallyAt ? "Clear manual mark" : "Mark as sent manually"
                }
              />
            </MenuItem>,
          ];
        })()}
      </Menu>

      <HubSpotMatchDialog
        target={hubspotMatchTarget}
        onClose={() => setHubspotMatchTarget(null)}
        onConfirm={(suggestion) => {
          // Review happens on the person's edit form — hand the picked
          // contact over via the query param (ADR-172).
          if (!hubspotMatchTarget) return;
          navigate(
            `/users/${hubspotMatchTarget.personId}?hubspotContact=${suggestion.contactId}`,
          );
        }}
      />
    </Box>
  );
}
